import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { FiUsers, FiUserCheck, FiUserX, FiChevronDown } from "react-icons/fi";
import { getCurrentUser } from "../../services/auth_service";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";
import {
  isSuperadmin,
  hasPermission,
} from "../utils/permissions";

import {
  getDrivers,
  deleteDriver,
  getLocations,
  getBranches,
  getStatuses,
  getDriverDocuments,
  uploadDriverDocument,
  getFinancialYears,
} from "../../services/driver_service";
import { getSupplierTypes } from "../../services/supplier_service";

import "./Drivers.css";

const DOC_HOST = "https://srinivasafleet.com/api";
const resolveUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${DOC_HOST}${path.startsWith("/") ? "" : "/"}${path}`;
};

const DOCUMENT_FIELDS = [
  { key: "adhaar_url", label: "Aadhaar Card" },
  { key: "license_file_url", label: "Driving License" },
  { key: "pancard_file_url", label: "Pancard" },
  { key: "bank_passbook_photo_url", label: "Bank Passbook" },
  { key: "gas_bill_photo_url", label: "Gas Bill" },
  { key: "electricity_bill_photo_url", label: "Electricity Bill" },
  { key: "driver_photo_url", label: "Driver Photo" },
];

const isPdf = (url) => url?.toLowerCase().includes(".pdf");
const isImageUrl = (url) => !!url && !isPdf(url);

const VIEW_KNOWN_KEYS = new Set([
  "id", "driver_code", "full_name", "mobile", "email",
  "aadhaar_number", "license_number", "license_expiry",
  "branch_id", "location_id", "character_nature", "blood_group",
  "is_active", "created_at", "updated_at", "financial_year_id",
  "supplier_type_id", "status_id",
  "date_of_birth", "joining_date",
  "address", "city", "state", "pincode",
  "permanent_address", "temporary_address",
  "pancard_number",
  "reference_person_name", "reference_contact_number_1", "reference_contact_number_2",
  "emergency_contact_name", "emergency_contact_number",
  "created_by", "updated_by", "created_by_name", "updated_by_name",
  ...DOCUMENT_FIELDS.map((f) => f.key),
]);

const HIDDEN_INTERNAL_KEYS = new Set(["user_id"]);

const isWithinFinancialYear = (createdAt, fy) => {
  if (!createdAt || !fy?.start_date || !fy?.end_date) return false;
  const created = new Date(createdAt);
  const start = new Date(fy.start_date);
  const end = new Date(fy.end_date);
  end.setHours(23, 59, 59, 999);
  return created >= start && created <= end;
};

const humanizeKey = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const toInputDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const formatDisplayDate = (value, { withTime = false } = {}) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const datePart = d.toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
  if (!withTime) return datePart;
  const timePart = d.toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit",
  });
  return `${datePart}, ${timePart}`;
};

const TOAST_AUTO_DISMISS_MS = 3200;

export default function Drivers() {
  const confirm = useConfirm();

  const canViewDrivers =
    isSuperadmin() || hasPermission("drivers", "view");
  const canEditDrivers =
    isSuperadmin() || hasPermission("drivers", "edit");
  const canDeleteDrivers =
    isSuperadmin() || hasPermission("drivers", "delete");
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const actionMenuRef = useRef(null);
  const [branches, setBranches] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [supplierTypes, setSupplierTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [headerVisible, setHeaderVisible] = useState(true);
  const [locationFilter, setLocationFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [bloodGroupFilter, setBloodGroupFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const filtersRef = useRef(null);
  const [financialYears, setFinancialYears] = useState([]);
  const [fyFilter, setFyFilter] = useState("");
  const [fyFromDate, setFyFromDate] = useState("");
  const [fyToDate, setFyToDate] = useState("");
  const [docModalDriver, setDocModalDriver] = useState(null);
  const [docModalData, setDocModalData] = useState(null);
  const [docModalLoading, setDocModalLoading] = useState(false);
  const [docModalError, setDocModalError] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [lightboxLabel, setLightboxLabel] = useState("");
  const [imgErrors, setImgErrors] = useState({});
  const [uploading, setUploading] = useState({});
  const [avatarErrors, setAvatarErrors] = useState({});
  const [viewDriver, setViewDriver] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [printing, setPrinting] = useState(false);
  const viewPrintRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const PAGE_SIZE = 10;
  const lastScrollY = useRef(0);
  const pageRef = useRef(null);
  const navigate = useNavigate();

  const showToast = useCallback((type, text) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, text });
    toastTimerRef.current = setTimeout(() => setToast(null), TOAST_AUTO_DISMISS_MS);
  }, []);

  const dismissToast = () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(null);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const handleScroll = useCallback(() => {
    const el = pageRef.current;
    if (!el) return;
    const currentY = el.scrollTop;
    if (currentY < 10) setHeaderVisible(true);
    else if (currentY > lastScrollY.current + 5) setHeaderVisible(false);
    else if (currentY < lastScrollY.current - 5) setHeaderVisible(true);
    lastScrollY.current = currentY;
  }, []);

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!fyFilter) {
      setFyFromDate("");
      setFyToDate("");
      return;
    }
    const fy = financialYears.find((y) => String(y.id) === String(fyFilter));
    setFyFromDate(toInputDate(fy?.start_date));
    setFyToDate(toInputDate(fy?.end_date));
  }, [fyFilter, financialYears]);

  useEffect(() => {
    fetchDrivers();
    fetchLocations();
    fetchStatuses();
    fetchFinancialYears();
    fetchSupplierTypes();
  }, []);

  useEffect(() => {
    fetchBranches(locationFilter || undefined);
    setBranchFilter("");
  }, [locationFilter]);

  useEffect(() => {
    if (!openActionMenu) return;

    const handleClickOutside = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setOpenActionMenu(null);
        setActionMenuPosition(null);
      }
    };

    const closeOnViewportChange = () => {
      setOpenActionMenu(null);
      setActionMenuPosition(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", closeOnViewportChange);
    window.addEventListener("scroll", closeOnViewportChange, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", closeOnViewportChange);
      window.removeEventListener("scroll", closeOnViewportChange, true);
    };
  }, [openActionMenu]);

  useEffect(() => {
    if (!showFilters) return;
    const handleClickOutside = (e) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target)) {
        setShowFilters(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilters]);

  useEffect(() => {
    let result = drivers;

    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      result = result.filter(
        (d) =>
          d.driver_code?.toLowerCase().includes(s) ||
          d.full_name?.toLowerCase().includes(s) ||
          d.mobile?.includes(s)
      );
    }

    if (locationFilter) {
      result = result.filter((d) => String(d.location_id) === String(locationFilter));
    }

    if (branchFilter) {
      result = result.filter((d) => String(d.branch_id) === String(branchFilter));
    }

    if (statusFilter) {
      const wantActive = statusFilter === "active";
      result = result.filter((d) => Boolean(d.is_active) === wantActive);
    }

    if (bloodGroupFilter) {
      result = result.filter((d) => d.blood_group === bloodGroupFilter);
    }

    if (fyFilter) {
      const fy = financialYears.find((y) => String(y.id) === String(fyFilter));

      result = result.filter((d) => {
        if (d.financial_year_id != null) {
          if (String(d.financial_year_id) !== String(fyFilter)) return false;
        } else {
          if (!isWithinFinancialYear(d.created_at, fy)) return false;
        }

        if (fyFromDate && fyToDate && d.created_at) {
          const created = new Date(d.created_at);
          const start = new Date(fyFromDate);
          const end = new Date(fyToDate);
          end.setHours(23, 59, 59, 999);
          if (created < start || created > end) return false;
        }

        return true;
      });
    }

    setFilteredDrivers(result);
    setCurrentPage(1);
  }, [searchTerm, drivers, locationFilter, branchFilter, statusFilter, bloodGroupFilter, fyFilter, fyFromDate, fyToDate, financialYears]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (lightboxUrl) { setLightboxUrl(null); setLightboxLabel(""); return; }
      if (openActionMenu) {
        setOpenActionMenu(null);
        setActionMenuPosition(null);
        return;
      }
      if (showFilters) { setShowFilters(false); return; }
      if (docModalDriver) { closeDocModal(); return; }
      if (viewDriver) closeViewModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxUrl, docModalDriver, viewDriver, openActionMenu, showFilters]);

  useEffect(() => {
    document.body.style.overflow = (docModalDriver || viewDriver) ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [docModalDriver, viewDriver]);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const data = await getDrivers();
      setDrivers(data);
      setFilteredDrivers(data);
    } catch (err) {
      console.error("Error fetching drivers:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try { setLocations(await getLocations() || []); } catch (e) { console.error(e); }
  };
  const fetchBranches = async (locationId) => {
    try { setBranches(await getBranches(locationId) || []); } catch (e) { console.error(e); }
  };
  const fetchStatuses = async () => {
    try {
      const r = await getStatuses();
      setStatuses(r.data || r || []);
    } catch (e) { console.error(e); }
  };
  const fetchFinancialYears = async () => {
    try { setFinancialYears(await getFinancialYears() || []); } catch (e) { console.error(e); }
  };
  const fetchSupplierTypes = async () => {
    try { setSupplierTypes(await getSupplierTypes() || []); } catch (e) { console.error(e); }
  };

  const getLocationName = (id) => locations.find((l) => l.id === id)?.location_name || "-";
  const getBranchName = (id) => branches.find((b) => b.id === id)?.branch_name || "-";

  const getSupplierTypeName = (id) => {
    if (id == null) return "-";
    return supplierTypes.find((t) => String(t.id) === String(id))?.supplier_type_name || "-";
  };

  const getStatusName = (id) => {
    if (id == null) return "-";
    return statuses.find((s) => String(s.id) === String(id))?.name || "-";
  };

  const getFinancialYearLabel = (driver) => {
    if (driver.financial_year_id != null) {
      const byId = financialYears.find((y) => String(y.id) === String(driver.financial_year_id));
      if (byId) return byId.fy_label;
      return "-";
    }
    const byDate = financialYears.find((y) => isWithinFinancialYear(driver.created_at, y));
    return byDate ? byDate.fy_label : "-";
  };

  const activeFilterCount = [
    searchTerm.trim() ? 1 : 0,
    locationFilter ? 1 : 0,
    branchFilter ? 1 : 0,
    statusFilter ? 1 : 0,
    bloodGroupFilter ? 1 : 0,
    fyFilter ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const handleReset = () => {
    setSearchTerm("");
    setLocationFilter("");
    setBranchFilter("");
    setStatusFilter("");
    setBloodGroupFilter("");
    setFyFilter("");
    setFilteredDrivers(drivers);
    setCurrentPage(1);
  };

  const bloodGroupOptions = Array.from(
    new Set(drivers.map((d) => d.blood_group).filter(Boolean))
  ).sort();

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: "Delete Driver",
      message: "Are you sure you want to delete this driver? This action cannot be undone.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      danger: true,
    });
    if (!confirmed) return;

    try {
      await deleteDriver(id);
      showToast("success", "Driver deleted successfully");
      fetchDrivers();
    } catch {
      showToast("error", "Delete failed. Please try again.");
    }
  };

  const openDocModal = async (driver) => {
    setImgErrors({});
    setUploading({});
    setDocModalDriver(driver);
    setDocModalData(null);
    setDocModalError(null);
    setDocModalLoading(true);
    try {
      const data = await getDriverDocuments(driver.id);
      setDocModalData(data);
    } catch (err) {
      console.error("Failed to fetch driver documents:", err);
      setDocModalError("Failed to load documents. Please try again.");
    } finally {
      setDocModalLoading(false);
    }
  };

  const closeDocModal = () => {
    setDocModalDriver(null);
    setDocModalData(null);
    setDocModalError(null);
    setImgErrors({});
    setUploading({});
  };

  const openLightbox = (url, label) => { setLightboxUrl(url); setLightboxLabel(label); };
  const closeLightbox = () => { setLightboxUrl(null); setLightboxLabel(""); };

  const openViewModal = async (driver) => {
    // Show the selected row immediately, then refresh the record so View /
    // Print / Download always use the latest driver data returned by the API.
    setViewDriver(driver);

    try {
      const [allDrivers, documentResponse] = await Promise.all([
        getDrivers(),
        getDriverDocuments(driver.id),
      ]);

      const freshDriver = (Array.isArray(allDrivers) ? allDrivers : []).find(
        (item) => String(item?.id) === String(driver.id)
      );

      const documentData = documentResponse?.documents || {};
      const responseDriver = documentResponse?.driver || documentResponse?.data || {};

      setViewDriver((current) => {
        if (!current || String(current.id) !== String(driver.id)) return current;
        return {
          ...current,
          ...(freshDriver || {}),
          ...(responseDriver || {}),
          ...(documentData || {}),
        };
      });
    } catch (error) {
      console.warn("Could not refresh driver details for View:", error);
    }
  };

  const closeViewModal = () => setViewDriver(null);
  const handleDocUpload = async (field, file) => {
    if (!docModalDriver) return;
    setUploading((prev) => ({ ...prev, [field]: true }));
    try {
      await uploadDriverDocument(docModalDriver.id, field, file);
      const data = await getDriverDocuments(docModalDriver.id);
      setDocModalData(data);
      setImgErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });

      if (field === "driver_photo_url") {
        setAvatarErrors((prev) => {
          const next = { ...prev };
          delete next[docModalDriver.id];
          return next;
        });
      }
    } catch (err) {
      console.error("Upload failed:", err);
      showToast("error", "Upload failed. Please try again.");
    } finally {
      setUploading((prev) => ({ ...prev, [field]: false }));
    }
  };

  const countUploadedDocs = (driver) =>
    DOCUMENT_FIELDS.reduce((n, { key }) => n + (driver[key] ? 1 : 0), 0);

  const totalDrivers = drivers.length;
  const activeDrivers = drivers.filter((d) => d.is_active).length;
  const inactiveDrivers = totalDrivers - activeDrivers;

  const totalPages = Math.max(1, Math.ceil(filteredDrivers.length / PAGE_SIZE));
  const paginatedDrivers = filteredDrivers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const goToPage = (page) => {
    setCurrentPage((prev) => {
      const next = Math.min(Math.max(1, page), totalPages);
      return next === prev ? prev : next;
    });
  };

  const loadImageAsDataUrl = async (url) => {
    if (!url) return null;
    try {
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) return null;
      const blob = await res.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result || null);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn("Image fetch error:", url, err);
      return null;
    }
  };

  const getImageDimensions = (dataUrl) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });

  const imageFormatFromDataUrl = (dataUrl) => {
    if (dataUrl.startsWith("data:image/png")) return "PNG";
    if (dataUrl.startsWith("data:image/webp")) return "WEBP";
    return "JPEG";
  };

  const buildDriverPdf = async (driver) => {
    const docImages = {};
    await Promise.all(
      DOCUMENT_FIELDS.map(async ({ key }) => {
        const rawUrl = driver[key];
        if (!rawUrl || isPdf(rawUrl)) return;
        const url = resolveUrl(rawUrl);
        const dataUrl = await loadImageAsDataUrl(url);
        if (!dataUrl) return;
        const dims = await getImageDimensions(dataUrl);
        if (dims) docImages[key] = { dataUrl, dims };
      })
    );

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 40;
    const contentWidth = pageWidth - marginX * 2;
    const footerY = pageHeight - 28;
    let y = 0;
    let pageNum = 1;

    const BRAND = [37, 99, 235];
    const LABEL_GRAY = [130, 130, 130];
    const VALUE_DARK = [30, 30, 30];
    const SECTION_GRAY = [90, 90, 90];
    const CARD_BG = [248, 249, 251];
    const CARD_BORDER = [230, 232, 236];

    const drawFooter = () => {
      doc.setFont(undefined, "normal");
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.text(`Page ${pageNum}`, pageWidth - marginX, footerY, { align: "right" });
      doc.text(`Generated ${new Date().toLocaleDateString("en-IN")}`, marginX, footerY);
    };

    const addPage = () => {
      drawFooter();
      doc.addPage();
      pageNum += 1;
      y = 40;
    };

    const ensureSpace = (needed) => {
      if (y + needed > footerY - 10) addPage();
    };

    const addSectionTitle = (title) => {
      ensureSpace(26);
      doc.setFillColor(...BRAND);
      doc.rect(marginX, y, 3, 12, "F");
      doc.setFont(undefined, "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(...SECTION_GRAY);
      doc.text(title.toUpperCase(), marginX + 10, y + 10);
      y += 22;
    };

    const addFieldGrid = (fields, gridWidth = contentWidth, columns = 2) => {
      const colGap = 16;
      const colWidth = (gridWidth - colGap * (columns - 1)) / columns;
      const rowPadY = 10;
      const labelSize = 8.2;
      const valueSize = 10;
      const lineH = 12.5;

      const rows = [];
      for (let i = 0; i < fields.length; i += columns) {
        rows.push(fields.slice(i, i + columns));
      }

      rows.forEach((row) => {
        doc.setFont(undefined, "normal");
        doc.setFontSize(valueSize);
        const wrappedCells = row.map((f) => {
          if (!f) return null;
          const text = f[1] === undefined || f[1] === null || f[1] === "" ? "-" : String(f[1]);
          return doc.splitTextToSize(text, colWidth - 4);
        });
        const rowLines = Math.max(...wrappedCells.map((w) => (w ? w.length : 1)));
        const rowHeight = labelSize + 4 + rowLines * lineH + rowPadY;

        ensureSpace(rowHeight);

        row.forEach((f, idx) => {
          if (!f) return;
          const cellX = marginX + idx * (colWidth + colGap);
          let cy = y;

          doc.setFont(undefined, "normal");
          doc.setFontSize(labelSize);
          doc.setTextColor(...LABEL_GRAY);
          doc.text(f[0].toUpperCase(), cellX, cy);
          cy += labelSize + 5;

          doc.setFont(undefined, "bold");
          doc.setFontSize(valueSize);
          doc.setTextColor(...VALUE_DARK);
          doc.text(wrappedCells[idx], cellX, cy);
        });

        y += rowHeight;
        doc.setDrawColor(...CARD_BORDER);
        doc.line(marginX, y - rowPadY / 2, marginX + gridWidth, y - rowPadY / 2);
      });

      y += 6;
    };

    const addDocumentGrid = () => {
      const cols = 3;
      const gap = 14;
      const cellW = (contentWidth - gap * (cols - 1)) / cols;
      const cellH = 130;
      const labelH = 16;

      let col = 0;
      let rowTopY = 0;

      const startRow = () => {
        ensureSpace(labelH + cellH + 22);
        rowTopY = y;
        col = 0;
      };
      startRow();

      DOCUMENT_FIELDS.forEach(({ key, label }) => {
        if (col === cols) {
          y = rowTopY + labelH + cellH + 22;
          startRow();
        }

        const cellX = marginX + col * (cellW + gap);

        doc.setFont(undefined, "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(...LABEL_GRAY);
        doc.text(label, cellX, rowTopY + 9);

        const boxY = rowTopY + labelH;
        doc.setDrawColor(...CARD_BORDER);
        doc.setFillColor(...CARD_BG);
        doc.roundedRect(cellX, boxY, cellW, cellH, 4, 4, "FD");

        const url = driver[key];
        const imgData = docImages[key];

        if (url && imgData) {
          const { dataUrl, dims } = imgData;
          const pad = 6;
          const maxW = cellW - pad * 2;
          const maxH = cellH - pad * 2;
          let w = dims.width, h = dims.height;
          const scale = Math.min(maxW / w, maxH / h, 1);
          w *= scale; h *= scale;
          const imgX = cellX + (cellW - w) / 2;
          const imgY = boxY + (cellH - h) / 2;
          const format = imageFormatFromDataUrl(dataUrl);
          try {
            doc.addImage(dataUrl, format, imgX, imgY, w, h);
          } catch {
            doc.setFont(undefined, "italic");
            doc.setFontSize(8);
            doc.setTextColor(...LABEL_GRAY);
            doc.text("Preview unavailable", cellX + 8, boxY + cellH / 2);
          }
        } else if (url) {
          doc.setFont(undefined, "italic");
          doc.setFontSize(8);
          doc.setTextColor(...LABEL_GRAY);
          doc.text("Uploaded — preview", cellX + 10, boxY + cellH / 2 - 5);
          doc.text("unavailable offline", cellX + 10, boxY + cellH / 2 + 7);
        } else {
          doc.setFont(undefined, "italic");
          doc.setFontSize(8.5);
          doc.setTextColor(190, 190, 190);
          doc.text("Not uploaded", cellX + 10, boxY + cellH / 2);
        }

        col += 1;
      });

      y = rowTopY + labelH + cellH + 22;
    };

    const bannerH = 78;
    doc.setFillColor(...BRAND);
    doc.rect(0, 0, pageWidth, bannerH, "F");

    const avatarR = 22;
    const avatarCx = marginX + avatarR;
    const avatarCy = bannerH / 2;
    const photoData = docImages["driver_photo_url"];
    if (photoData) {
      try {
        const format = imageFormatFromDataUrl(photoData.dataUrl);
        doc.addImage(
          photoData.dataUrl, format,
          avatarCx - avatarR, avatarCy - avatarR, avatarR * 2, avatarR * 2
        );
      } catch { }
    } else {
      doc.setFillColor(255, 255, 255);
      doc.circle(avatarCx, avatarCy, avatarR, "F");
      doc.setFont(undefined, "bold");
      doc.setFontSize(16);
      doc.setTextColor(...BRAND);
      const initial = (driver.full_name || "D").trim().charAt(0).toUpperCase();
      doc.text(initial, avatarCx, avatarCy + 5, { align: "center" });
    }

    const textX = marginX + avatarR * 2 + 16;
    doc.setFont(undefined, "bold");
    doc.setFontSize(17);
    doc.setTextColor(255, 255, 255);
    doc.text(driver.full_name || "Driver", textX, bannerH / 2 - 6);

    doc.setFont(undefined, "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(225, 231, 250);
    doc.text(
      `${driver.driver_code || "-"}   ·   ${driver.mobile || "-"}   ·   ${getBranchName(driver.branch_id)}`,
      textX, bannerH / 2 + 13
    );

    const statusText = driver.is_active ? "Active" : "Inactive";
    doc.setFont(undefined, "bold");
    doc.setFontSize(9);
    const pillPadX = 10;
    const pillW = doc.getTextWidth(statusText) + pillPadX * 2;
    const pillX = pageWidth - marginX - pillW;
    const pillY = bannerH / 2 - 9;
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(pillX, pillY, pillW, 18, 9, 9, "F");
    doc.setTextColor(...BRAND);
    doc.text(statusText, pillX + pillW / 2, pillY + 12.5, { align: "center" });

    y = bannerH + 26;

    addSectionTitle("Contact");

    const photoBoxW = 110;
    const photoBoxH = 130;
    const photoBoxGap = 18;
    const photoBoxX = marginX + contentWidth - photoBoxW;
    ensureSpace(photoBoxH + 10);
    const boxY = y;

    doc.setDrawColor(...CARD_BORDER);
    doc.setFillColor(...CARD_BG);
    doc.roundedRect(photoBoxX, boxY, photoBoxW, photoBoxH, 4, 4, "FD");

    const driverPhoto = docImages["driver_photo_url"];
    if (driverPhoto) {
      const { dataUrl, dims } = driverPhoto;
      const pad = 6;
      const maxW = photoBoxW - pad * 2;
      const maxH = photoBoxH - pad * 2;
      let w = dims.width, h = dims.height;
      const scale = Math.min(maxW / w, maxH / h, 1);
      w *= scale; h *= scale;
      const imgX = photoBoxX + (photoBoxW - w) / 2;
      const imgY = boxY + (photoBoxH - h) / 2;
      try {
        const format = imageFormatFromDataUrl(dataUrl);
        doc.addImage(dataUrl, format, imgX, imgY, w, h);
      } catch { }
    } else {
      doc.setFont(undefined, "italic");
      doc.setFontSize(8.5);
      doc.setTextColor(190, 190, 190);
      doc.text("No photo", photoBoxX + photoBoxW / 2, boxY + photoBoxH / 2, { align: "center" });
    }

    doc.setFont(undefined, "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...LABEL_GRAY);
    doc.text("DRIVER PHOTO", photoBoxX + photoBoxW / 2, boxY + photoBoxH + 11, { align: "center" });

    const contactGridWidth = contentWidth - photoBoxW - photoBoxGap;
    addFieldGrid([
      ["Driver Code", driver.driver_code],
      ["Full Name", driver.full_name],
      ["Mobile", driver.mobile],
      ["Email", driver.email],
      ["Aadhaar Number", driver.aadhaar_number],
      ["Blood Group", driver.blood_group],
      ["Driver Type", getSupplierTypeName(driver.supplier_type_id)],
      ["Date of Birth", formatDisplayDate(driver.date_of_birth)],
    ], contactGridWidth);

    const photoBottom = boxY + photoBoxH + 20;
    if (y < photoBottom) y = photoBottom;

    addSectionTitle("License");
    addFieldGrid([
      ["License Number", driver.license_number],
      ["License Expiry", formatDisplayDate(driver.license_expiry)],
      ["Character / Nature", driver.character_nature],
      ["Pancard Number", driver.pancard_number],
    ], contentWidth, 3);

    addSectionTitle("Address");
    addFieldGrid([
      ["Permanent Address", driver.permanent_address],
      ["Temporary Address", driver.temporary_address],
      ["City", driver.city],
      ["State", driver.state],
      ["Pincode", driver.pincode],
      ["Joining Date", formatDisplayDate(driver.joining_date)],
    ], contentWidth, 3);

    addSectionTitle("Reference & Emergency Contact");
    addFieldGrid([
      ["Reference Person", driver.reference_person_name],
      ["Reference Contact 1", driver.reference_contact_number_1],
      ["Reference Contact 2", driver.reference_contact_number_2],
      ["Emergency Contact Name", driver.emergency_contact_name],
      ["Emergency Contact Number", driver.emergency_contact_number],
    ], contentWidth, 3);

    addSectionTitle("Location & Status");
    addFieldGrid([
      ["Branch", getBranchName(driver.branch_id)],
      ["Location", getLocationName(driver.location_id)],
      ["Financial Year", getFinancialYearLabel(driver)],
      ["Driver Status", getStatusName(driver.status_id)],
      ["Active", statusText],
    ], contentWidth, 3);

    addSectionTitle("Audit");
    addFieldGrid([
      ["Created By", driver.created_by_name],
      ["Created At", formatDisplayDate(driver.created_at, { withTime: true })],
      ["Updated By", driver.updated_by_name],
      ["Updated At", formatDisplayDate(driver.updated_at, { withTime: true })],
    ], contentWidth, 3);

    addSectionTitle("Documents");
    addDocumentGrid();

    const extraEntries = Object.entries(driver).filter(
      ([k, v]) =>
        !VIEW_KNOWN_KEYS.has(k) &&
        !HIDDEN_INTERNAL_KEYS.has(k) &&
        v !== null && v !== undefined && v !== ""
    );
    if (extraEntries.length > 0) {
      addSectionTitle("Additional Details");
      addFieldGrid(
        extraEntries.map(([k, v]) => [
          humanizeKey(k),
          typeof v === "object" ? JSON.stringify(v) : String(v),
        ]),
        contentWidth, 3
      );
    }

    drawFooter();
    return doc;
  };

  const getDriverPdfFileName = (driver) => {
    const safeName = (driver?.driver_code || driver?.full_name || "driver")
      .toString()
      .trim()
      .replace(/[^a-zA-Z0-9_-]+/g, "_")
      .replace(/^_+|_+$/g, "");

    return `${safeName || "driver"}.pdf`;
  };

  /*
   * IMPORTANT:
   * Print and Download must use the COMPLETE View modal.
   * The normal View modal has a scrollable body. If we simply clone it,
   * html2canvas/browser print captures only the visible portion.
   *
   * This function converts the modal into a temporary "document mode":
   * - no max-height
   * - no scrollbars
   * - no mask/gradient clipping
   * - full body height
   * - full documents grid
   * - buttons/close controls removed
   */
  const prepareDriverPrintClone = async () => {
    if (!viewPrintRef.current) {
      throw new Error("Driver view is not available");
    }

    const source = viewPrintRef.current;
    const clone = source.cloneNode(true);

    clone.classList.add("driver-print-complete-record");

    clone.querySelectorAll(
      ".view-driver-header-actions, " +
      ".driver-modal-close-btn, " +
      ".driver-doc-thumb-overlay, " +
      ".driver-doc-pdf-actions, " +
      ".driver-doc-upload-label, " +
      ".no-print"
    ).forEach((el) => el.remove());

    const setImportant = (el, property, value) => {
      if (el) el.style.setProperty(property, value, "important");
    };

    // The modal itself must become a normal full-height document.
    setImportant(clone, "width", "100%");
    setImportant(clone, "max-width", "none");
    setImportant(clone, "height", "auto");
    setImportant(clone, "max-height", "none");
    setImportant(clone, "min-height", "0");
    setImportant(clone, "display", "block");
    setImportant(clone, "position", "static");
    setImportant(clone, "overflow", "visible");
    setImportant(clone, "margin", "0");
    setImportant(clone, "border-radius", "0");
    setImportant(clone, "box-shadow", "none");
    setImportant(clone, "transform", "none");
    setImportant(clone, "animation", "none");
    setImportant(clone, "background", "#ffffff");

    const body = clone.querySelector(".view-driver-body");
    if (body) {
      setImportant(body, "height", "auto");
      setImportant(body, "max-height", "none");
      setImportant(body, "min-height", "0");
      setImportant(body, "overflow", "visible");
      setImportant(body, "overflow-y", "visible");
      setImportant(body, "overflow-x", "visible");
      setImportant(body, "-webkit-mask-image", "none");
      setImportant(body, "mask-image", "none");
      setImportant(body, "display", "block");
      setImportant(body, "background", "#ffffff");
    }

    const docsGrid = clone.querySelector(".driver-docs-grid");
    if (docsGrid) {
      setImportant(docsGrid, "height", "auto");
      setImportant(docsGrid, "max-height", "none");
      setImportant(docsGrid, "overflow", "visible");
      setImportant(docsGrid, "overflow-y", "visible");
      setImportant(docsGrid, "display", "grid");
    }

    clone.querySelectorAll(".view-driver-section").forEach((section) => {
      setImportant(section, "height", "auto");
      setImportant(section, "max-height", "none");
      setImportant(section, "overflow", "visible");
      setImportant(section, "break-inside", "avoid");
      setImportant(section, "page-break-inside", "avoid");
    });

    clone.querySelectorAll(".driver-doc-tile").forEach((tile) => {
      setImportant(tile, "height", "auto");
      setImportant(tile, "overflow", "visible");
      setImportant(tile, "break-inside", "avoid");
      setImportant(tile, "page-break-inside", "avoid");
    });

    // Make all images visible and convert remote images to data URLs.
    const sourceImages = Array.from(source.querySelectorAll("img"));
    const cloneImages = Array.from(clone.querySelectorAll("img"));

    await Promise.all(
      cloneImages.map(async (img, index) => {
        const sourceImg = sourceImages[index];
        const src = sourceImg?.currentSrc || sourceImg?.src;
        if (!src) return;

        setImportant(img, "opacity", "1");
        setImportant(img, "visibility", "visible");
        setImportant(img, "display", "block");

        try {
          const dataUrl = await loadImageAsDataUrl(src);
          if (dataUrl) {
            img.src = dataUrl;
            img.removeAttribute("srcset");
            setImportant(img, "opacity", "1");
          }
        } catch (error) {
          console.warn("Could not inline driver image:", src, error);
        }
      })
    );

    // Remove links from the print/download copy.
    clone.querySelectorAll("a").forEach((a) => {
      const span = document.createElement("span");
      span.textContent = a.textContent?.trim() || "";
      span.style.color = "inherit";
      span.style.textDecoration = "none";
      a.replaceWith(span);
    });

    clone.querySelectorAll(".driver-status-pill").forEach((el) => {
      setImportant(el, "print-color-adjust", "exact");
      setImportant(el, "-webkit-print-color-adjust", "exact");
    });

    return clone;
  };

  const waitForImages = async (root) => {
    const images = Array.from(root?.querySelectorAll?.("img") || []);

    await Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete && img.naturalWidth > 0) {
              resolve();
              return;
            }

            const done = () => {
              img.removeEventListener("load", done);
              img.removeEventListener("error", done);
              resolve();
            };

            img.addEventListener("load", done, { once: true });
            img.addEventListener("error", done, { once: true });

            setTimeout(done, 4000);
          })
      )
    );
  };

  const handleDownloadView = async () => {
    if (!viewDriver || pdfGenerating) return;

    setPdfGenerating(true);
    let host = null;

    try {
      const clone = await prepareDriverPrintClone();

      host = document.createElement("div");
      host.className = "driver-pdf-render-host";

      host.style.setProperty("position", "fixed", "important");
      host.style.setProperty("left", "-100000px", "important");
      host.style.setProperty("top", "0", "important");
      host.style.setProperty("width", "794px", "important");
      host.style.setProperty("height", "auto", "important");
      host.style.setProperty("max-height", "none", "important");
      host.style.setProperty("overflow", "visible", "important");
      host.style.setProperty("padding", "0", "important");
      host.style.setProperty("margin", "0", "important");
      host.style.setProperty("background", "#ffffff", "important");
      host.style.setProperty("z-index", "-1", "important");

      host.appendChild(clone);
      document.body.appendChild(host);

      await new Promise((resolve) =>
        requestAnimationFrame(() =>
          requestAnimationFrame(resolve)
        )
      );

      await waitForImages(clone);

      const fullWidth = Math.max(
        clone.scrollWidth,
        clone.offsetWidth,
        794
      );

      const fullHeight = Math.max(
        clone.scrollHeight,
        clone.offsetHeight
      );

      // Explicitly lock the clone to its complete content dimensions.
      clone.style.setProperty("width", `${fullWidth}px`, "important");
      clone.style.setProperty("height", `${fullHeight}px`, "important");
      clone.style.setProperty("max-height", "none", "important");
      clone.style.setProperty("overflow", "visible", "important");

      await new Promise((resolve) =>
        requestAnimationFrame(() =>
          requestAnimationFrame(resolve)
        )
      );

      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        imageTimeout: 20000,
        width: fullWidth,
        height: fullHeight,
        windowWidth: Math.max(fullWidth, 1200),
        scrollX: 0,
        scrollY: 0,
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 18;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;

      const pxPerPage = Math.max(
        1,
        Math.floor((usableHeight / usableWidth) * canvas.width)
      );

      let sourceY = 0;
      let pageNumber = 0;

      while (sourceY < canvas.height) {
        const sliceHeight = Math.min(
          pxPerPage,
          canvas.height - sourceY
        );

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;

        const ctx = pageCanvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(
          0,
          0,
          pageCanvas.width,
          pageCanvas.height
        );

        ctx.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sliceHeight,
          0,
          0,
          pageCanvas.width,
          pageCanvas.height
        );

        const imageData = pageCanvas.toDataURL(
          "image/jpeg",
          0.96
        );

        const renderedHeight =
          (sliceHeight / canvas.width) * usableWidth;

        if (pageNumber > 0) {
          pdf.addPage();
        }

        pdf.addImage(
          imageData,
          "JPEG",
          margin,
          margin,
          usableWidth,
          renderedHeight,
          undefined,
          "FAST"
        );

        sourceY += sliceHeight;
        pageNumber += 1;
      }

      pdf.save(getDriverPdfFileName(viewDriver));

      showToast(
        "success",
        "Complete driver details downloaded successfully"
      );
    } catch (error) {
      console.error(
        "Complete driver PDF download failed:",
        error
      );

      showToast(
        "error",
        "Failed to download complete driver details. Please try again."
      );
    } finally {
      if (host?.parentNode) {
        host.parentNode.removeChild(host);
      }

      setPdfGenerating(false);
    }
  };

  const handlePrintView = async () => {
    if (!viewDriver || printing) return;

    setPrinting(true);

    const printWindow = window.open("", "_blank", "width=1200,height=900");

    if (!printWindow) {
      setPrinting(false);
      showToast("error", "Please allow pop-ups for printing the driver details.");
      return;
    }

    let renderHost = null;

    try {
      /*
       * Build the print copy inside the CURRENT application first.
       * This is important because Drivers.css contains selectors such as
       * .driver-page-container .view-driver-field. A clone placed directly
       * under <body> loses those styles.
       */
      const sourceClone = await prepareDriverPrintClone();

      renderHost = document.createElement("div");
      renderHost.className = "driver-print-render-root";
      Object.assign(renderHost.style, {
        position: "fixed",
        left: "-100000px",
        top: "0",
        width: "794px",
        minHeight: "1123px",
        height: "auto",
        overflow: "visible",
        background: "#ffffff",
        zIndex: "-1",
        visibility: "visible",
      });

      const printPageContainer = document.createElement("div");
      printPageContainer.className = "driver-page-container";
      Object.assign(printPageContainer.style, {
        width: "794px",
        maxWidth: "794px",
        minHeight: "0",
        height: "auto",
        maxHeight: "none",
        overflow: "visible",
        padding: "0",
        margin: "0",
        background: "#ffffff",
        display: "block",
      });

      printPageContainer.appendChild(sourceClone);
      renderHost.appendChild(printPageContainer);
      document.body.appendChild(renderHost);

      // Force the clone to the exact printable width and remove all scroll limits.
      const force = (el, prop, value) => {
        if (el) el.style.setProperty(prop, value, "important");
      };

      force(sourceClone, "width", "794px");
      force(sourceClone, "max-width", "794px");
      force(sourceClone, "height", "auto");
      force(sourceClone, "max-height", "none");
      force(sourceClone, "overflow", "visible");
      force(sourceClone, "display", "block");
      force(sourceClone, "position", "static");
      force(sourceClone, "background", "#ffffff");

      const body = sourceClone.querySelector(".view-driver-body");
      if (body) {
        force(body, "height", "auto");
        force(body, "max-height", "none");
        force(body, "overflow", "visible");
        force(body, "overflow-y", "visible");
        force(body, "overflow-x", "visible");
        force(body, "display", "block");
        force(body, "background", "#ffffff");
      }

      sourceClone.querySelectorAll(".view-driver-section").forEach((el) => {
        force(el, "height", "auto");
        force(el, "max-height", "none");
        force(el, "overflow", "visible");
        force(el, "break-inside", "avoid-page");
        force(el, "page-break-inside", "avoid");
      });

      sourceClone.querySelectorAll(".driver-docs-grid").forEach((el) => {
        force(el, "height", "auto");
        force(el, "max-height", "none");
        force(el, "overflow", "visible");
        force(el, "display", "grid");
      });

      await waitForImages(sourceClone);

      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      );

      /*
       * Copy computed styles into inline styles. This makes the print page
       * independent of CSS selector ancestry and fixes the blank-print issue.
       */
      const copyComputedStyles = (from, to) => {
        if (!from || !to || from.nodeType !== 1 || to.nodeType !== 1) return;

        const computed = window.getComputedStyle(from);
        const importantProperties = [
          "box-sizing", "display", "position", "top", "right", "bottom", "left",
          "width", "min-width", "max-width", "height", "min-height", "max-height",
          "margin", "padding", "border", "border-top", "border-right",
          "border-bottom", "border-left", "border-radius", "background",
          "background-color", "background-image", "background-size",
          "background-position", "color", "font-family", "font-size", "font-weight",
          "font-style", "line-height", "letter-spacing", "text-align",
          "text-transform", "white-space", "word-break", "overflow",
          "overflow-x", "overflow-y", "grid-template-columns", "grid-template-rows",
          "grid-column-gap", "grid-row-gap", "gap", "column-gap", "row-gap",
          "flex-direction", "flex-wrap", "align-items", "align-content",
          "justify-content", "justify-items", "flex", "flex-grow", "flex-shrink",
          "grid-column", "grid-row", "object-fit", "object-position", "opacity",
          "box-shadow", "visibility"
        ];

        importantProperties.forEach((property) => {
          const value = computed.getPropertyValue(property);
          if (value) to.style.setProperty(property, value);
        });

        const fromChildren = Array.from(from.children);
        const toChildren = Array.from(to.children);
        fromChildren.forEach((child, index) => {
          copyComputedStyles(child, toChildren[index]);
        });
      };

      const printClone = sourceClone.cloneNode(true);
      copyComputedStyles(sourceClone, printClone);

      // Images are already data URLs from prepareDriverPrintClone().
      await waitForImages(printClone);

      const printDocument = printWindow.document;
      printDocument.open();
      printDocument.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${getDriverPdfFileName(viewDriver).replace(/\.pdf$/i, "")}</title>
<style>
@page { size: A4 portrait; margin: 8mm; }
html, body {
  margin: 0 !important;
  padding: 0 !important;
  width: 100% !important;
  min-height: 0 !important;
  background: #fff !important;
}
body {
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important;
}
.driver-print-page {
  width: 100% !important;
  max-width: 100% !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  background: #fff !important;
}
.view-driver-modal,
.driver-modal-card,
.driver-print-complete-record {
  position: static !important;
  width: 100% !important;
  max-width: 100% !important;
  height: auto !important;
  max-height: none !important;
  min-height: 0 !important;
  overflow: visible !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  transform: none !important;
  background: #fff !important;
}
.view-driver-body {
  height: auto !important;
  max-height: none !important;
  min-height: 0 !important;
  overflow: visible !important;
  padding: 12px !important;
  background: #fff !important;
}
.view-driver-section {
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  break-inside: avoid-page !important;
  page-break-inside: avoid !important;
}
.view-driver-field {
  break-inside: avoid !important;
  page-break-inside: avoid !important;
}
.driver-docs-grid {
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  display: grid !important;
}
.driver-doc-tile {
  break-inside: avoid !important;
  page-break-inside: avoid !important;
  overflow: visible !important;
}
img {
  opacity: 1 !important;
  visibility: visible !important;
  print-color-adjust: exact !important;
  -webkit-print-color-adjust: exact !important;
}
.driver-doc-thumb-img { display: block !important; opacity: 1 !important; }
.view-driver-header-actions,
.driver-modal-close-btn,
.driver-doc-thumb-overlay,
.driver-doc-pdf-actions,
.driver-doc-upload-label,
.no-print { display: none !important; }
</style>
</head>
<body></body>
</html>`);
      printDocument.close();

      const page = printDocument.createElement("div");
      page.className = "driver-print-page";
      page.appendChild(printClone);
      printDocument.body.appendChild(page);

      try {
        if (printDocument.fonts?.ready) await printDocument.fonts.ready;
      } catch (_) {}

      await waitForImages(printDocument);
      await new Promise((resolve) => setTimeout(resolve, 1200));

      printWindow.focus();
      printWindow.onafterprint = () => {
        setTimeout(() => {
          try { printWindow.close(); } catch (_) {}
        }, 150);
      };

      printWindow.print();
    } catch (error) {
      console.error("Complete driver print failed:", error);
      try { printWindow.close(); } catch (_) {}
      showToast("error", "Print could not be prepared. Please try again.");
    } finally {
      if (renderHost?.parentNode) renderHost.parentNode.removeChild(renderHost);
      setPrinting(false);
    }
  };

  const toggleActionMenu = (e, driverId) => {
    e.stopPropagation();

    if (openActionMenu === driverId) {
      setOpenActionMenu(null);
      setActionMenuPosition(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = window.innerWidth <= 600 ? 125 : 132;
    const menuItems =
      (canViewDrivers ? 1 : 0) +
      (canEditDrivers ? 1 : 0) +
      (canDeleteDrivers ? 1 : 0);
    const menuHeight = 18 + menuItems * 40;
    const gap = 5;
    const screenPadding = 8;

    let left = rect.right - menuWidth;
    let top = rect.bottom + gap;

    if (left < screenPadding) {
      left = screenPadding;
    }

    if (left + menuWidth > window.innerWidth - screenPadding) {
      left = window.innerWidth - menuWidth - screenPadding;
    }

    if (top + menuHeight > window.innerHeight - screenPadding) {
      top = rect.top - menuHeight - gap;
    }

    if (top < screenPadding) {
      top = screenPadding;
    }

    setActionMenuPosition({ top, left });
    setOpenActionMenu(driverId);
  };

  return (
    <div className="driver-layout">
      <Sidebar />
      <div className="driver-content">

        <div className={`driver-header-wrapper ${headerVisible ? "driver-header-visible" : "driver-header-hidden"}`}>
          <Header />
        </div>

        <div className="driver-page-container" ref={pageRef}>

          <div className="driver-stats-bar">
            <div className="driver-stat-card">
              <div className="driver-stat-icon">
                <FiUsers />
              </div>
              <div className="driver-stat-text">
                <span className="driver-stat-value">{totalDrivers}</span>
                <span className="driver-stat-label">Total Drivers</span>
              </div>
            </div>

            <div className="driver-stat-card driver-stat-card--active">
              <div className="driver-stat-icon">
                <FiUserCheck />
              </div>
              <div className="driver-stat-text">
                <span className="driver-stat-value">{activeDrivers}</span>
                <span className="driver-stat-label">Active</span>
              </div>
            </div>

            <div className="driver-stat-card driver-stat-card--inactive">
              <div className="driver-stat-icon">
                <FiUserX />
              </div>
              <div className="driver-stat-text">
                <span className="driver-stat-value">{inactiveDrivers}</span>
                <span className="driver-stat-label">Inactive</span>
              </div>
            </div>
          </div>

          <div className="driver-toolbar">
            <div className="driver-search-group">
              <span className="driver-search-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </span>
              <input
                type="text"
                className="driver-search-input"
                placeholder="Search by code, name or mobile…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="driver-clear-btn" onClick={() => setSearchTerm("")} title="Clear search">✕</button>
              )}
            </div>

            <div className="driver-fy-filter-field">
              <select
                value={fyFilter}
                onChange={(e) => setFyFilter(e.target.value)}
                title="Filter drivers by financial year"
              >
                <option value="">All Financial Years</option>
                {financialYears.map((y) => (
                  <option key={y.id} value={y.id}>{y.fy_label}</option>
                ))}
              </select>

              {fyFilter && (
                <div className="driver-fy-date-range">
                  <label>
                    From
                    <input
                      type="date"
                      value={fyFromDate}
                      onChange={(e) => setFyFromDate(e.target.value)}
                    />
                  </label>
                  <span className="driver-fy-date-range-arrow">→</span>
                  <label>
                    To
                    <input
                      type="date"
                      value={fyToDate}
                      onChange={(e) => setFyToDate(e.target.value)}
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="driver-filter-wrap" ref={filtersRef}>
              <button
                type="button"
                className={`driver-filter-toggle-btn ${showFilters ? "driver-filter-toggle-btn--active" : ""}`}
                onClick={() => setShowFilters((v) => !v)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                Filters
                {activeFilterCount > 0 && (
                  <span className="driver-filter-count-badge">{activeFilterCount}</span>
                )}
              </button>

              {showFilters && (
                <div className="driver-filters-row">
                  <div className="driver-filter-field">
                    <label>Location</label>
                    <select
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                    >
                      <option value="">All Locations</option>
                      {locations.map((l) => (
                        <option key={l.id} value={l.id}>{l.location_name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="driver-filter-field">
                    <label>Branch</label>
                    <select
                      value={branchFilter}
                      onChange={(e) => setBranchFilter(e.target.value)}
                    >
                      <option value="">All Branches</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="driver-filter-field">
                    <label>Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="driver-filter-field">
                    <label>Blood Group</label>
                    <select
                      value={bloodGroupFilter}
                      onChange={(e) => setBloodGroupFilter(e.target.value)}
                    >
                      <option value="">All Groups</option>
                      {bloodGroupOptions.map((bg) => (
                        <option key={bg} value={bg}>{bg}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    className="driver-filter-panel-done"
                    onClick={() => setShowFilters(false)}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>

            {activeFilterCount > 0 && (
              <button className="driver-reset-btn" onClick={handleReset}>
                Reset
              </button>
            )}

            {(canViewDrivers || canEditDrivers || canDeleteDrivers) && (
              <button className="driver-primary-btn" onClick={() => navigate("/add-driver")}>
                <span>+</span> Add Driver
              </button>
            )}
          </div>

          <div className="driver-table-card">
            <div className="driver-table-scroll">
              <table className="driver-data-table">
                <thead>
                  <tr>
                    <th>Photo</th>
                    <th>Branch</th>
                    <th>Driver Code</th>
                    <th>Full Name</th>
                    <th>Mobile</th>
                    <th>Character</th>
                    <th>Status</th>
                    {(canViewDrivers || canEditDrivers || canDeleteDrivers) && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="driver-state-cell">
                        <div className="driver-spinner" /> Loading drivers…
                      </td>
                    </tr>
                  ) : filteredDrivers.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="driver-state-cell">
                        <div className="driver-empty-icon">🚗</div> No drivers found
                      </td>
                    </tr>
                  ) : (
                    paginatedDrivers.map((driver) => {
                      const avatarUrl = resolveUrl(driver.driver_photo_url);
                      const avatarFailed = avatarErrors[driver.id];

                      return (
                        <tr
                          key={driver.id}
                          className={canViewDrivers ? "driver-row-clickable" : ""}
                          onClick={() => {
                            if (canViewDrivers) openViewModal(driver);
                          }}
                          title={canViewDrivers ? "Click to view full details" : ""}
                        >
                          <td>
                            <div className="driver-photo-cell">
                              {avatarUrl && !avatarFailed ? (
                                <img
                                  src={avatarUrl}
                                  alt={driver.full_name}
                                  className="driver-photo-thumb"
                                  loading="lazy"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openLightbox(avatarUrl, `${driver.full_name} — Photo`);
                                  }}
                                  onError={() =>
                                    setAvatarErrors((prev) => ({ ...prev, [driver.id]: true }))
                                  }
                                />
                              ) : (
                                <div className="driver-photo-fallback">
                                  {driver.full_name?.charAt(0)?.toUpperCase() || "?"}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{getBranchName(driver.branch_id)}</td>
                          <td><span className="driver-code-badge">{driver.driver_code}</span></td>
                          <td className="driver-name-cell">{driver.full_name}</td>
                          <td className="driver-mobile-cell">{driver.mobile}</td>
                          <td><span className="driver-character-badge">{driver.character_nature}</span></td>
                          <td>
                            <span className={`driver-status-pill ${driver.is_active ? "driver-status-pill--active" : "driver-status-pill--inactive"}`}>
                              <span className="driver-status-dot" />
                              {driver.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          {(canViewDrivers || canEditDrivers || canDeleteDrivers) && (
                            <td onClick={(e) => e.stopPropagation()}>
                              <div className="driver-action-menu-wrapper">
                                <button
                                  type="button"
                                  className={`driver-actions-btn ${openActionMenu === driver.id ? "driver-actions-btn--active" : ""}`}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => toggleActionMenu(e, driver.id)}
                                  aria-label="Row actions"
                                  aria-haspopup="true"
                                  aria-expanded={openActionMenu === driver.id}
                                >
                                  Actions
                                  <FiChevronDown size={14} />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {!loading && filteredDrivers.length > 0 && (
              <div className="driver-table-footer driver-table-footer--paginated">
                <span className="driver-table-footer-count">
                  Showing <strong>{(currentPage - 1) * PAGE_SIZE + 1}</strong>–
                  <strong>{Math.min(currentPage * PAGE_SIZE, filteredDrivers.length)}</strong> of{" "}
                  <strong>{filteredDrivers.length}</strong>
                  <span className="driver-pagination-summary-suffix"> drivers</span>
                </span>

                <div className="driver-pagination-controls">
                  <button
                    type="button"
                    className="driver-pagination-arrow-btn"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <span className="driver-pagination-page-label">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="driver-pagination-arrow-btn"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {openActionMenu &&
        actionMenuPosition &&
        createPortal(
          <div
            ref={actionMenuRef}
            className="driver-action-menu-dropdown driver-action-menu-dropdown--portal"
            style={{
              position: "fixed",
              top: `${actionMenuPosition.top}px`,
              left: `${actionMenuPosition.left}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {canViewDrivers && (
              <button
                type="button"
                className="driver-action-menu-item driver-action-menu-item--view"
                onClick={() => {
                  const driverId = openActionMenu;
                  setOpenActionMenu(null);
                  setActionMenuPosition(null);
                  navigate(`/edit-driver/${driverId}?readOnly=true`);
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span>View</span>
              </button>
            )}

            {canEditDrivers && (
              <>
                {(canViewDrivers || canDeleteDrivers) && <div className="driver-action-menu-divider" />}
                <button
                  type="button"
                  className="driver-action-menu-item"
                  onClick={() => {
                    const driverId = openActionMenu;
                    setOpenActionMenu(null);
                    setActionMenuPosition(null);
                    navigate(`/edit-driver/${driverId}`);
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1-1-4Z" />
                  </svg>
                  <span>Edit</span>
                </button>
              </>
            )}

            {canDeleteDrivers && (
              <>
                {(canViewDrivers || canEditDrivers) && <div className="driver-action-menu-divider" />}
                <button
                  type="button"
                  className="driver-action-menu-item driver-action-menu-item--delete"
                  onClick={() => {
                    const driverId = openActionMenu;
                    setOpenActionMenu(null);
                    setActionMenuPosition(null);
                    handleDelete(driverId);
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18" />
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6" />
                  </svg>
                  <span>Delete</span>
                </button>
              </>
            )}
          </div>,
          document.body
        )}

      {viewDriver && (
        <div className="driver-modal-overlay" onClick={closeViewModal}>
          <div ref={viewPrintRef} className="driver-modal-card view-driver-modal" onClick={(e) => e.stopPropagation()}>

            <div className="driver-modal-header view-driver-header">
              <div className="view-driver-header-left">
                {resolveUrl(viewDriver.driver_photo_url) && !avatarErrors[viewDriver.id] ? (
                  <img
                    src={resolveUrl(viewDriver.driver_photo_url)}
                    alt={viewDriver.full_name}
                    className="view-driver-avatar"
                  />
                ) : (
                  <div className="view-driver-avatar view-driver-avatar--fallback">
                    {viewDriver.full_name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                )}
                <div>
                  <h3 className="driver-modal-title">{viewDriver.full_name || "Driver"}</h3>
                  <p className="driver-modal-code">
                    {viewDriver.driver_code}
                    {viewDriver.mobile && <>&nbsp;·&nbsp;{viewDriver.mobile}</>}
                  </p>
                </div>
              </div>
              <div className="view-driver-header-actions">
                <button
                  type="button"
                  className="driver-action-btn view-driver-btn view-driver-btn--outline"
                  onClick={handlePrintView}
                  disabled={printing}
                >
                  {printing ? "Preparing…" : "🖨 Print"}
                </button>
                <button
                  type="button"
                  className="driver-action-btn view-driver-btn view-driver-btn--solid"
                  onClick={handleDownloadView}
                  disabled={pdfGenerating}
                >
                  {pdfGenerating ? "Preparing…" : "⬇ Download"}
                </button>
                <button
                  type="button"
                  className="driver-modal-close-btn"
                  onClick={closeViewModal}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="view-driver-body">
              <div className="view-driver-section">
                <h4>Contact</h4>
                <div className="view-driver-grid">
                  <div className="view-driver-field"><span>Driver Code</span><strong>{viewDriver.driver_code || "-"}</strong></div>
                  <div className="view-driver-field"><span>Full Name</span><strong>{viewDriver.full_name || "-"}</strong></div>
                  <div className="view-driver-field"><span>Mobile</span><strong>{viewDriver.mobile || "-"}</strong></div>
                  <div className="view-driver-field"><span>Email</span><strong>{viewDriver.email || "-"}</strong></div>
                  <div className="view-driver-field"><span>Aadhaar Number</span><strong>{viewDriver.aadhaar_number || "-"}</strong></div>
                  <div className="view-driver-field"><span>Blood Group</span><strong>{viewDriver.blood_group || "-"}</strong></div>
                  <div className="view-driver-field"><span>Driver Type</span><strong>{getSupplierTypeName(viewDriver.supplier_type_id)}</strong></div>
                  <div className="view-driver-field"><span>Date of Birth</span><strong>{formatDisplayDate(viewDriver.date_of_birth)}</strong></div>
                </div>
              </div>

              <div className="view-driver-section">
                <h4>License</h4>
                <div className="view-driver-grid">
                  <div className="view-driver-field"><span>License Number</span><strong>{viewDriver.license_number || "-"}</strong></div>
                  <div className="view-driver-field"><span>License Expiry</span><strong>{formatDisplayDate(viewDriver.license_expiry)}</strong></div>
                  <div className="view-driver-field"><span>Character / Nature</span><strong>{viewDriver.character_nature || "-"}</strong></div>
                  <div className="view-driver-field"><span>Pancard Number</span><strong>{viewDriver.pancard_number || "-"}</strong></div>
                </div>
              </div>

              <div className="view-driver-section">
                <h4>Address</h4>
                <div className="view-driver-grid">
                  <div className="view-driver-field"><span>Permanent Address</span><strong>{viewDriver.permanent_address || "-"}</strong></div>
                  <div className="view-driver-field"><span>Temporary Address</span><strong>{viewDriver.temporary_address || "-"}</strong></div>
                  <div className="view-driver-field"><span>City</span><strong>{viewDriver.city || "-"}</strong></div>
                  <div className="view-driver-field"><span>State</span><strong>{viewDriver.state || "-"}</strong></div>
                  <div className="view-driver-field"><span>Pincode</span><strong>{viewDriver.pincode || "-"}</strong></div>
                  <div className="view-driver-field"><span>Joining Date</span><strong>{formatDisplayDate(viewDriver.joining_date)}</strong></div>
                </div>
              </div>

              <div className="view-driver-section">
                <h4>Reference &amp; Emergency Contact</h4>
                <div className="view-driver-grid">
                  <div className="view-driver-field"><span>Reference Person</span><strong>{viewDriver.reference_person_name || "-"}</strong></div>
                  <div className="view-driver-field"><span>Reference Contact 1</span><strong>{viewDriver.reference_contact_number_1 || "-"}</strong></div>
                  <div className="view-driver-field"><span>Reference Contact 2</span><strong>{viewDriver.reference_contact_number_2 || "-"}</strong></div>
                  <div className="view-driver-field"><span>Emergency Contact Name</span><strong>{viewDriver.emergency_contact_name || "-"}</strong></div>
                  <div className="view-driver-field"><span>Emergency Contact Number</span><strong>{viewDriver.emergency_contact_number || "-"}</strong></div>
                </div>
              </div>

              <div className="view-driver-section">
                <h4>Location &amp; Status</h4>
                <div className="view-driver-grid">
                  <div className="view-driver-field"><span>Branch</span><strong>{getBranchName(viewDriver.branch_id)}</strong></div>
                  <div className="view-driver-field"><span>Location</span><strong>{getLocationName(viewDriver.location_id)}</strong></div>
                  <div className="view-driver-field"><span>Financial Year</span><strong>{getFinancialYearLabel(viewDriver)}</strong></div>
                  <div className="view-driver-field"><span>Driver Status</span><strong>{getStatusName(viewDriver.status_id)}</strong></div>
                  <div className="view-driver-field">
                    <span>Active</span>
                    <strong>
                      <span className={`driver-status-pill ${viewDriver.is_active ? "driver-status-pill--active" : "driver-status-pill--inactive"}`}>
                        <span className="driver-status-dot" />
                        {viewDriver.is_active ? "Active" : "Inactive"}
                      </span>
                    </strong>
                  </div>
                </div>
              </div>

              <div className="view-driver-section">
                <h4>Audit</h4>
                <div className="view-driver-grid">
                  <div className="view-driver-field"><span>Created By</span><strong>{viewDriver.created_by_name || "-"}</strong></div>
                  <div className="view-driver-field"><span>Created On</span><strong>{formatDisplayDate(viewDriver.created_at, { withTime: true })}</strong></div>
                  <div className="view-driver-field"><span>Updated By</span><strong>{viewDriver.updated_by_name || "-"}</strong></div>
                  <div className="view-driver-field"><span>Updated On</span><strong>{formatDisplayDate(viewDriver.updated_at, { withTime: true })}</strong></div>
                </div>
              </div>

              <div className="view-driver-section">
                <h4>Documents</h4>
                <div className="driver-docs-grid">
                  {DOCUMENT_FIELDS.map(({ key, label }) => {
                    const rawUrl = viewDriver[key];
                    const url = resolveUrl(rawUrl);

                    return (
                      <div className="driver-doc-tile" key={key}>
                        <span className="driver-doc-tile-label">{label}</span>

                        {!url && (
                          <div className="driver-doc-tile-empty">
                            <span className="driver-doc-empty-icon">📂</span>
                            <span>Not uploaded</span>
                          </div>
                        )}

                        {url && isPdf(url) && (
                          <div className="driver-doc-tile-pdf">
                            <span className="driver-doc-pdf-icon">📄</span>
                            <div className="driver-doc-pdf-actions">
                              <a href={url} target="_blank" rel="noopener noreferrer" className="driver-doc-pdf-link">
                                Open PDF ↗
                              </a>
                              <a href={url} download className="driver-doc-pdf-link driver-doc-pdf-download">
                                ⬇ Download
                              </a>
                            </div>
                          </div>
                        )}

                        {url && isImageUrl(url) && (
                          <button
                            type="button"
                            className="driver-doc-thumb-btn"
                            onClick={() => openLightbox(url, label)}
                            title={`View ${label}`}
                          >
                            <img
                              src={url}
                              alt={label}
                              className="driver-doc-thumb-img"
                              onLoad={(e) => { e.target.style.opacity = "1"; }}
                              style={{ opacity: 0, transition: "opacity 0.25s" }}
                            />
                            <div className="driver-doc-thumb-overlay">
                              <span className="driver-doc-thumb-zoom">🔍 View</span>
                            </div>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>

          </div>
        </div>
      )}

      {docModalDriver && (
        <div className="driver-modal-overlay" onClick={closeDocModal}>
          <div className="driver-modal-card driver-docs-modal" onClick={(e) => e.stopPropagation()}>

            <div className="driver-modal-header">
              <div>
                <h3 className="driver-modal-title">
                  Documents
                  <span className="driver-modal-subtitle"> — {docModalDriver.full_name}</span>
                </h3>
                <p className="driver-modal-code">{docModalDriver.driver_code}</p>
              </div>
              <button
                type="button"
                className="driver-modal-close-btn"
                onClick={closeDocModal}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {docModalLoading && (
              <div className="driver-docs-modal-loading">
                <div className="driver-spinner" />
                <span>Loading documents…</span>
              </div>
            )}

            {!docModalLoading && docModalError && (
              <div className="driver-docs-modal-error">
                <span>⚠️ {docModalError}</span>
                <button type="button" className="driver-action-btn" onClick={() => openDocModal(docModalDriver)}>
                  Retry
                </button>
              </div>
            )}

            {!docModalLoading && !docModalError && docModalData && (
              <div className="driver-docs-grid">
                {DOCUMENT_FIELDS.map(({ key, label }) => {
                  const rawUrl = docModalData.documents?.[key];
                  const url = resolveUrl(rawUrl);
                  const hasError = imgErrors[key];
                  const isBusy = uploading[key];

                  return (
                    <div className="driver-doc-tile" key={key}>
                      <span className="driver-doc-tile-label">{label}</span>

                      {!url && !hasError && (
                        <div className="driver-doc-tile-empty">
                          <span className="driver-doc-empty-icon">📂</span>
                          <span>Not uploaded</span>
                        </div>
                      )}

                      {url && !hasError && isPdf(url) && (
                        <div className="driver-doc-tile-pdf">
                          <span className="driver-doc-pdf-icon">📄</span>
                          <div className="driver-doc-pdf-actions">
                            <a href={url} target="_blank" rel="noopener noreferrer" className="driver-doc-pdf-link">
                              Open PDF ↗
                            </a>
                            <a href={url} download className="driver-doc-pdf-link driver-doc-pdf-download">
                              ⬇ Download
                            </a>
                          </div>
                        </div>
                      )}

                      {url && !hasError && !isPdf(url) && (
                        <button
                          type="button"
                          className="driver-doc-thumb-btn"
                          onClick={() => openLightbox(url, label)}
                          title={`View ${label}`}
                        >
                          <img
                            src={url}
                            alt={label}
                            className="driver-doc-thumb-img"
                            onLoad={(e) => { e.target.style.opacity = "1"; }}
                            onError={() => setImgErrors((prev) => ({ ...prev, [key]: true }))}
                            style={{ opacity: 0, transition: "opacity 0.25s" }}
                          />
                          <div className="driver-doc-thumb-overlay">
                            <span className="driver-doc-thumb-zoom">🔍 View</span>
                          </div>
                        </button>
                      )}

                      {url && hasError && (
                        <div className="driver-doc-tile-error">
                          <span className="driver-doc-error-icon">⚠️</span>
                          <span>Failed to load image</span>
                          <a href={url} target="_blank" rel="noopener noreferrer" className="driver-doc-error-link">
                            Open file ↗
                          </a>
                        </div>
                      )}

                      <label className={`driver-doc-upload-label ${isBusy ? "driver-doc-upload-label--busy" : ""} ${!canEditDrivers ? "driver-doc-upload-label--disabled" : ""}`}>
                        {isBusy ? (
                          <>
                            <div className="driver-spinner driver-spinner--sm" />
                            Uploading…
                          </>
                        ) : (
                          <>{url ? "♻ Replace" : "⬆ Upload"}</>
                        )}
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          style={{ display: "none" }}
                          disabled={isBusy || !canEditDrivers}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleDocUpload(key, file);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
            )}

            {!docModalLoading && !docModalError && docModalData && (
              <div className="driver-docs-modal-footer">
                {docModalData.total_uploaded} of {docModalData.total_fields} documents uploaded
              </div>
            )}

          </div>
        </div>
      )}

      {lightboxUrl && (
        <div className="driver-lightbox-overlay" onClick={closeLightbox}>
          <div className="driver-lightbox-toolbar">
            <span className="driver-lightbox-label">{lightboxLabel}</span>
            <div className="driver-lightbox-actions">

              <button
                type="button"
                className="driver-lightbox-close-btn"
                onClick={closeLightbox}
              >
                ✕
              </button>
            </div>
          </div>
          <img
            src={lightboxUrl}
            alt={lightboxLabel}
            className="driver-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="driver-lightbox-hint">Press Esc or click outside to close</p>
        </div>
      )}

      {toast && (
        <div
          className="driver-toast-overlay"
          style={{
            position: "fixed",
            bottom: "28px",
            right: "28px",
            zIndex: 3000,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "14px 18px",
            borderRadius: "10px",
            minWidth: "260px",
            maxWidth: "380px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            color: "#fff",
            fontSize: "14px",
            fontWeight: 500,
            background: toast.type === "success" ? "#16a34a" : "#dc2626",
            animation: "driverToastSlideIn 0.25s ease-out",
          }}
        >
          <span style={{ fontSize: "16px", lineHeight: 1 }}>
            {toast.type === "success" ? "✓" : "⚠"}
          </span>
          <span style={{ flex: 1 }}>{toast.text}</span>
          <button
            type="button"
            onClick={dismissToast}
            aria-label="Dismiss"
            style={{
              background: "transparent",
              border: "none",
              color: "#fff",
              opacity: 0.85,
              cursor: "pointer",
              fontSize: "15px",
              lineHeight: 1,
              padding: "2px",
            }}
          >
            ✕
          </button>
        </div>
      )}
      <style>{`
        @keyframes driverToastSlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

    </div>
  );
}